import React, { useCallback, useEffect, useState } from "react";
import { KeyRound, Lock, Plus, ShieldCheck, Trash2, UserCog, X, RotateCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/AppLayout";
import PermissionsModal from "@/components/users/PermissionsModal";
import DangerZoneReset from "@/components/users/DangerZoneReset";
import ResetPasswordModal from "@/components/users/ResetPasswordModal";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";
import { PERM, hasPerm } from "@/lib/permissions";
import { toast } from "sonner";

const ROLES = [
  { v: "admin", label: "Admin" },
  { v: "sme_reviewer", label: "SME Reviewer" },
  { v: "coordinator", label: "Coordinator" },
];

const ROLE_LABEL = {
  super_admin: "Super Admin",
  admin: "Admin",
  sme_reviewer: "SME Reviewer",
  coordinator: "Coordinator",
};

export default function Users() {
  const { user: actor } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [unlocking, setUnlocking] = useState(null);
  const [permsFor, setPermsFor] = useState(null);
  const [resettingPw, setResettingPw] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  const canManageUsers = hasPerm(actor, PERM.USERS_MANAGE);
  const canUnlock = hasPerm(actor, PERM.USERS_LOCK_UNLOCK);
  const canGrantPerms = hasPerm(actor, PERM.PERMISSIONS_GRANT);
  const canReset = hasPerm(actor, PERM.DATA_RESET);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const del = (u) => {
    setConfirmState({
      title: "Delete this user?",
      message: (
        <span>
          You&apos;re about to permanently delete <strong>{u.name}</strong> ({u.email}). Any batches
          they currently own will keep referencing them by name in audit history.
        </span>
      ),
      confirmLabel: "Delete user",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/users/${u.id}`);
          toast.success("User deleted");
          load();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
      },
    });
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Users"
        subtitle="Provision, lock and manage permissions for Admin, SME Reviewer and Coordinator accounts."
        actions={
          canManageUsers ? (
            <button
              data-testid="add-user-btn"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold"
            >
              <Plus className="w-4 h-4" /> Add User
            </button>
          ) : null
        }
      />

      <div className="bg-white rounded-2xl border border-slate-100 brand-shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Role</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-50">
                <td className="px-5 py-3.5">
                  <div className="font-semibold text-slate-900">{u.name}</div>
                </td>
                <td className="px-5 py-3.5 text-slate-700 font-mono text-xs">{u.email}</td>
                <td className="px-5 py-3.5">
                  <span
                    className={`px-2 py-0.5 rounded-md text-[11px] font-mono uppercase tracking-wider ${
                      u.role === "super_admin"
                        ? "bg-orange-50 text-orange-700"
                        : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {ROLE_LABEL[u.role] || u.role}
                  </span>
                </td>
                <td className="px-5 py-3.5">
                  {u.is_locked ? (
                    <span
                      data-testid={`locked-badge-${u.id}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-mono uppercase tracking-wider bg-amber-50 text-amber-700"
                      title={`Locked after 3 failed logins${u.locked_at ? " on " + new Date(u.locked_at).toLocaleString() : ""}`}
                    >
                      <Lock className="w-3 h-3" /> Locked
                    </span>
                  ) : (
                    <button
                      onClick={() => toggleActive(u)}
                      disabled={u.role === "super_admin"}
                      className={`px-2 py-0.5 rounded-md text-[11px] font-mono uppercase tracking-wider ${
                        u.is_active
                          ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {u.is_active ? "Active" : "Disabled"}
                    </button>
                  )}
                </td>
                <td className="px-5 py-3.5">
                  <div className="flex items-center justify-end gap-1">
                    {u.is_locked && canUnlock && (
                      <button
                        data-testid={`unlock-btn-${u.id}`}
                        onClick={() => setUnlocking(u)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100"
                      >
                        <KeyRound className="w-3.5 h-3.5" /> Unlock &amp; Reset
                      </button>
                    )}
                    {canGrantPerms && u.role !== "super_admin" && (
                      <button
                        data-testid={`perms-btn-${u.id}`}
                        onClick={() => setPermsFor(u)}
                        title="Manage permissions"
                        className="p-1.5 rounded-md text-slate-400 hover:bg-orange-50 hover:text-orange-600"
                      >
                        <ShieldCheck className="w-4 h-4" />
                      </button>
                    )}
                    {canManageUsers && u.role !== "super_admin" && (
                      <button
                        data-testid={`reset-pw-btn-${u.id}`}
                        onClick={() => setResettingPw(u)}
                        title="Reset password"
                        className="p-1.5 rounded-md text-slate-400 hover:bg-orange-50 hover:text-orange-600"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    )}
                    {canManageUsers && u.role !== "super_admin" && (
                      <button
                        onClick={() => del(u)}
                        data-testid={`delete-user-${u.id}`}
                        className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-12 text-center text-sm text-slate-400">
                  <UserCog className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  No users yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && <CreateUserModal onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />}
      {unlocking && (
        <UnlockUserModal
          user={unlocking}
          onClose={() => setUnlocking(null)}
          onUnlocked={() => {
            setUnlocking(null);
            load();
          }}
        />
      )}
      {permsFor && (
        <PermissionsModal
          user={permsFor}
          onClose={() => setPermsFor(null)}
          onSaved={() => {
            setPermsFor(null);
            load();
          }}
        />
      )}
      {resettingPw && (
        <ResetPasswordModal
          user={resettingPw}
          onClose={() => setResettingPw(null)}
          onDone={load}
        />
      )}
      {canReset && <DangerZoneReset onDone={load} />}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </PageContainer>
  );
}

function UnlockUserModal({ user, onClose, onUnlocked }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/users/${user.id}/unlock`, { password });
      toast.success(`Unlocked ${user.email}. New password set.`);
      onUnlocked();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <form
        onSubmit={submit}
        data-testid="unlock-modal"
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Unlock &amp; Reset Password
          </h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2.5 mb-4 text-xs text-amber-800 leading-relaxed">
          <strong>{user.email}</strong> was locked after 3 failed login attempts. Set a new
          password below — they&apos;ll be able to sign in immediately with it.
        </div>
        <div>
          <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
            New Password
          </label>
          <input
            required
            minLength={8}
            type="text"
            value={password}
            data-testid="unlock-password-input"
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none font-mono"
          />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="unlock-submit-btn"
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:bg-slate-200 text-white text-sm font-semibold"
          >
            <KeyRound className="w-4 h-4" />
            {busy ? "Unlocking…" : "Unlock & Reset"}
          </button>
        </div>
      </form>
    </div>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("sme_reviewer");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", { name, email: email.trim(), role, password });
      toast.success("User created");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <form
        onSubmit={submit}
        data-testid="create-user-modal"
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Add User
          </h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
              Full Name
            </label>
            <input
              required
              data-testid="user-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
              Email
            </label>
            <input
              required
              type="email"
              data-testid="user-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
              Role
            </label>
            <select
              data-testid="user-role-select"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
            >
              {ROLES.map((r) => (
                <option key={r.v} value={r.v}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
              Password
            </label>
            <input
              required
              minLength={8}
              type="text"
              data-testid="user-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none font-mono"
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="user-submit-btn"
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:bg-slate-200 text-white text-sm font-semibold"
          >
            {busy ? "Creating…" : "Create User"}
          </button>
        </div>
      </form>
    </div>
  );
}
